import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, AlertCircle, Shield, Eye, HeadphonesIcon, Lock, Activity, Building2, CheckCircle, Wifi, WifiOff, RefreshCw, UserPlus, DollarSign, Zap, Users, ArrowRight, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import PublicLayout from '../components/PublicLayout';       // v3.15-1169A — single source of truth for public layout

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, token, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // v3.14-85: Connection diagnostic state
  const [connectionStatus, setConnectionStatus] = useState(null); // null | 'checking' | 'connected' | 'failed'
  const [connectionDetails, setConnectionDetails] = useState(null);
  // v3.15-1180: Become a Partner modal removed — contained invented marketing copy not approved by user.

  // v3.14-120: Check if already authenticated on mount - use localStorage directly
  useEffect(() => {
    const storedToken = sessionStorage.getItem('access_token')
                     || localStorage.getItem('access_token');
    console.log('[LoginPage v3.18-1333] Mount check, storedToken:', storedToken ? 'EXISTS' : 'NULL');

    if (storedToken) {
      // Ask the backend where this user should land. Internal ops
      // users go to /calendar, everyone else to /dashboard. The hint
      // comes from ``GET /auth/me`` (``landing_page``) so we never
      // hard-code the route on the client.
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      fetch(`${backendUrl}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${storedToken}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(u => {
          // v3.18-1388 CRM-INT-005 — /dashboard is CEO-only.  Default
          // fallback for other users is the workspace landing (/) which
          // in turn routes them to /clients.
          const target = (u && u.landing_page) || '/';
          navigate(target, { replace: true });
        })
        .catch(() => navigate('/', { replace: true }));
    }
  }, []); // Only run on mount - no dependencies to prevent loops

  // v3.14-85: Auto-check connection on mount and when error occurs
  useEffect(() => {
    // Auto-run connection check on page load
    testConnection();
  }, []);

  // v3.14-85: Connection test function - runs automatically and on demand
  const testConnection = async () => {
    setConnectionStatus('checking');
    setConnectionDetails(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const startTime = Date.now();
      const response = await fetch(`${backendUrl}/api/version`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const duration = Date.now() - startTime;
      
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus('connected');
        setConnectionDetails({
          version: data.version,
          latency: `${duration}ms`,
          backendUrl: backendUrl,
          timestamp: new Date().toLocaleTimeString()
        });
      } else {
        setConnectionStatus('failed');
        setConnectionDetails({
          error: `HTTP ${response.status}`,
          backendUrl: backendUrl,
          timestamp: new Date().toLocaleTimeString()
        });
      }
    } catch (err) {
      setConnectionStatus('failed');
      setConnectionDetails({
        error: err.message || 'Network Error',
        backendUrl: backendUrl,
        possibleCauses: [
          'Backend server not running',
          'CORS not configured for this domain',
          'Network/firewall blocking requests',
          'SSL certificate issues'
        ],
        timestamp: new Date().toLocaleTimeString()
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('[LoginPage v3.14-120] Login attempt for:', email);
      
      // v3.14-120: Login and verify token storage
      const result = await login(email, password);
      
      // v3.15-1181: token is written to sessionStorage by AuthContext.login()
      // (XSS-safe migration from v3.15-578). The previous read against
      // localStorage always returned null, breaking every login. Read the
      // same store that was just written.
      const storedToken = sessionStorage.getItem('access_token');
      console.log('[LoginPage v3.15-1181] TOKEN SAVED:', storedToken ? 'YES' : 'NO');
      console.log('[LoginPage v3.15-1181] TOKEN READ:', storedToken?.substring(0, 20) + '...');
      
      if (!storedToken) {
        throw new Error('Token storage failed - please try again');
      }
      
      toast.success('Welcome back!');

      // v3.18-1333: Route internal ops users to /calendar (their
      // operational home page) and everyone else to /dashboard.
      // The target is a backend-issued hint in ``result.user.landing_page``
      // so the client never hard-codes internal-company logic.
      // v3.18-1388 CRM-INT-005 — /dashboard is CEO-only; fallback is /
      // (WorkspaceLanding routes non-CEO users to /clients).
      const target = result?.user?.landing_page || '/';
      console.log('[LoginPage v3.18-1333] Navigating to', target);

      // Use setTimeout to ensure state updates propagate before navigation
      setTimeout(() => {
        navigate(target, { replace: true });
      }, 100);
      
    } catch (err) {
      // v3.21-1769 · Route suspended accounts to the dedicated screen
      // BEFORE surfacing a generic invalid-credential toast.  Backend
      // returns HTTP 403 + detail={code:'ACCOUNT_SUSPENDED', message}
      // on POST /api/auth/login for a suspended user with valid creds.
      try {
        const _det = err?.response?.data?.detail;
        const _isSuspended =
          err?.response?.status === 403 &&
          _det && typeof _det === 'object' &&
          _det.code === 'ACCOUNT_SUSPENDED';
        if (_isSuspended) {
          try {
            sessionStorage.removeItem('access_token');
            localStorage.removeItem('access_token');
          } catch (_e) { /* noop */ }
          navigate('/suspended', { replace: true });
          return;
        }
      } catch (_e) { /* noop */ }

      // v3.14-83: Enhanced error logging for debugging
      console.error('Login error details:', {
        message: err?.message,
        code: err?.code,
        response: err?.response?.data,
        status: err?.response?.status,
        config: {
          url: err?.config?.url,
          baseURL: err?.config?.baseURL
        }
      });
      
      const errorMsg = getApiErrorMessage(err, 'Invalid credentials. Please try again.');
      
      // Show additional diagnostic info if it's a network error
      if (err?.message?.includes('Network Error')) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'NOT_SET';
        console.error('NETWORK ERROR - Diagnostic info:', {
          backendUrl,
          attemptedUrl: `${backendUrl}/api/auth/login`,
          tips: [
            '1. Check if backend is running: curl ' + backendUrl + '/api/health',
            '2. Check browser Network tab for the failing request',
            '3. Verify CORS allows this domain',
            '4. Check for mixed HTTP/HTTPS content'
          ]
        });
      }
      
      setError(errorMsg);
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  return (
    <PublicLayout testId="login-page">
      <div className="flex flex-col lg:flex-row lg:items-start">
        {/* v3.15-1174 — Hero LEFT: editorial single-column. Restores and
            expands the PTC Client Hub + EasyQuickFund layout the partner
            base trusts. Long-form, relationship-driven, sells confidence —
            not software. Right-side login card remains untouched and goes
            sticky on desktop so it stays accessible while the editorial
            column scrolls. */}
        <div className="w-full lg:w-1/2 relative bg-white">
          <div className="px-8 lg:px-14 py-16 lg:py-20 max-w-2xl mx-auto lg:mx-0 lg:ml-auto">

            {/* Brand chain — three products on one line */}
            <p className="font-heading text-base lg:text-lg font-bold tracking-tight text-primary leading-snug animate-fade-in">
              PTC Client Hub <span className="text-gold mx-1">+</span> EasyQuickFund <span className="text-gold mx-1">+</span> CreditForward360
            </p>
            <div className="mt-3 h-[1px] w-20 bg-gradient-gold" />

            {/* Section eyebrow */}
            <p className="mt-8 text-[11px] uppercase tracking-[0.3em] text-slate-500 animate-fade-in">
              Partner Financial Infrastructure
            </p>

            {/* Primary hook */}
            <h1 className="mt-4 font-heading text-3xl lg:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight text-primary animate-fade-in">
              Need funding solutions<br />
              <span className="text-gradient-gold">for your clients?</span>
            </h1>

            {/* Body — three relationship paragraphs */}
            <div className="mt-8 space-y-5 text-[15px] text-slate-700 leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <p>
                Prestigious Companies provides approved partners with access to our proprietary Client Hub and integrated financial infrastructure designed to simplify every stage of the funding process.
              </p>
              <p>
                From the moment you introduce a client, our dedicated Application Concierge manages the file from submission through funding while keeping you informed with real-time visibility inside Prestigious Hub.
              </p>
              <p>
                Whether your client needs consumer financing, business funding, or credit improvement before becoming fundable, every service operates inside one connected platform.
              </p>
            </div>

            {/* Our Ecosystem */}
            <div className="mt-16 animate-slide-up" style={{ animationDelay: '0.15s' }}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-3">Our Ecosystem</p>
              <div className="h-[1px] w-16 bg-gradient-gold mb-10" />

              <div className="space-y-9">
                <div data-testid="eco-hub">
                  <h3 className="font-heading text-xl lg:text-2xl font-bold text-primary mb-2">Prestigious Hub</h3>
                  <p className="text-[15px] text-slate-700 leading-relaxed">
                    The central operating platform where every application, conversation, document, and funding milestone is managed.
                  </p>
                </div>

                <div data-testid="eco-eqf">
                  <h3 className="font-heading text-xl lg:text-2xl font-bold text-primary mb-2">
                    EasyQuickFund<sup className="text-[10px] text-gold ml-0.5">™</sup>
                  </h3>
                  <p className="text-[15px] text-slate-700 leading-relaxed">
                    Consumer financing through our nationwide lending network with financing options up to $100,000 for qualified borrowers.
                  </p>
                </div>

                <div data-testid="eco-biz">
                  <h3 className="font-heading text-xl lg:text-2xl font-bold text-primary mb-2">Business Funding</h3>
                  <p className="text-[15px] text-slate-700 leading-relaxed">
                    Commercial financing solutions including working capital, equipment financing, term loans, lines of credit, revenue-based financing, and SBA programs.
                  </p>
                </div>

                <div data-testid="eco-cf360">
                  <h3 className="font-heading text-xl lg:text-2xl font-bold text-primary mb-2">CreditForward360</h3>
                  <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed">
                    <p className="font-medium text-slate-900">Not every client qualifies today.</p>
                    <p>That should never mean losing the relationship.</p>
                    <p>
                      If a client isn&rsquo;t approved for the funding they need, CreditForward360 automatically becomes part of the process.
                    </p>
                    <p>
                      Using our proprietary workflow and proven credit improvement strategies, we work directly with the client to strengthen their credit profile while keeping everything connected inside Prestigious Hub.
                    </p>
                    <p>
                      When they&rsquo;re ready, the client moves back into the funding process without starting over.
                    </p>
                    <p className="text-slate-900">Instead of losing the deal&hellip;</p>
                    <p className="text-slate-900 font-medium">&hellip;you gain another opportunity to close it.</p>
                    <p className="text-sm text-slate-500 italic">
                      Better for your client. Better for your business.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ghost™ AI Operations Assistant — premium standalone block */}
            <div className="mt-16 bg-slate-950 text-white rounded-lg p-8 lg:p-10 relative overflow-hidden animate-slide-up" style={{ animationDelay: '0.2s' }} data-testid="hero-ghost-block">
              <div aria-hidden className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-gold/[0.08] rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-start gap-4 mb-5">
                  <img src="/marketing/ghost-mark.png" alt="" aria-hidden="true" className="h-12 w-12 object-contain shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">AI Operations Assistant</p>
                    <h3 className="font-heading text-2xl lg:text-3xl font-bold leading-tight">
                      Ghost<sup className="text-[12px] text-gold ml-0.5">™</sup>
                    </h3>
                  </div>
                </div>
                <p className="text-slate-300 text-[15px] leading-relaxed mb-4">
                  Ghost works alongside every Application Concierge to monitor activity, identify opportunities, recommend next steps, surface important updates, and help ensure no opportunity falls through the cracks.
                </p>
                <p className="text-slate-200 text-[15px] leading-relaxed">
                  Ghost doesn&rsquo;t replace people.
                </p>
                <p className="text-white font-medium text-[15px] leading-relaxed">
                  Ghost makes our people even better.
                </p>
              </div>
            </div>

            {/* Partner Benefits */}
            <div className="mt-16 animate-slide-up" style={{ animationDelay: '0.25s' }} data-testid="hero-partner-benefits">
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-3">Partner Benefits</p>
              <div className="h-[1px] w-16 bg-gradient-gold mb-8" />
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[15px] text-slate-700">
                {[
                  'Secure partner access',
                  'Real-time deal visibility',
                  'Dedicated Application Concierge',
                  'Consumer Funding',
                  'Business Funding',
                  'CreditForward360 recovery path',
                  'Ghost™ AI Operations Assistant',
                  'Internal funding specialists',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2.5 leading-snug">
                    <span aria-hidden className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Closing statement */}
            <div className="mt-16 border-l-4 border-gold pl-6 animate-slide-up" style={{ animationDelay: '0.3s' }} data-testid="hero-closing">
              <p className="text-[15px] lg:text-base text-slate-800 leading-relaxed">
                Prestigious Companies doesn&rsquo;t just help partners fund clients.
                We provide an entire financial ecosystem that helps them close more business — <strong className="text-primary">even when the answer isn&rsquo;t &ldquo;yes&rdquo; on day one.</strong>
              </p>
            </div>

            {/* Partnership Inquiries */}
            <div className="mt-16 pt-10 border-t border-slate-200 animate-slide-up" style={{ animationDelay: '0.35s' }} data-testid="hero-partnership-inquiries">
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-3">Partnership Inquiries</p>
              <a
                href="mailto:support@prestigiouscompanies.org"
                className="text-base font-medium text-primary hover:text-slate-700 transition-colors block mb-5"
                data-testid="hero-contact-email"
              >
                support@prestigiouscompanies.org
              </a>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
                <a href="https://easyquickfund.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">easyquickfund.com</a>
                <a href="https://creditforward360.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">creditforward360.com</a>
                <span className="text-slate-700">hub.prestigiouscompanies.org</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login Form (v3.15-1174: sticky on desktop so it
            stays accessible while the editorial column scrolls). */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50 lg:sticky lg:top-24 lg:self-start lg:min-h-[calc(100vh-6rem)]">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-primary">
                Prestigious Hub
              </h1>
              <div className="mx-auto mt-2 h-[2px] w-10 bg-gradient-gold rounded-full" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mt-3">
                Financial Concierge Platform
              </p>
            </div>

            <Card className="border-slate-100 shadow-soft">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl">Partner Login</CardTitle>
                <CardDescription>
                  Enter your credentials to access your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="login-error">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                      data-testid="email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11"
                      data-testid="password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-primary hover:bg-slate-800"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </form>

                {/* Forgot Password Link */}
                <div className="mt-4 text-center">
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-primary hover:text-primary/80 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </a>
                </div>

                {/* Secure Platform Notice */}
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="h-4 w-4 text-slate-400" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Secure Platform Notice
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-slate-500">256-bit SSL encrypted connection</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-slate-500">Secure partner authentication</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-slate-500">Activity monitoring and fraud protection</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-slate-500">Powered by Prestigious Companies Financial Infrastructure</span>
                    </div>
                  </div>
                </div>

                {/* Help text for partners */}
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-sm text-slate-500 text-center">
                    Need help accessing your account?
                  </p>
                  <p className="text-xs text-slate-400 text-center mt-2">
                    Contact your Prestigious Companies representative for assistance.
                  </p>
                </div>

                {/* Footer Line */}
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400 text-center tracking-wide">
                    Authorized Partner Platform • Prestigious Companies
                  </p>
                  {/* Legal & Privacy Links + Connection Status */}
                  <div className="flex justify-center items-center gap-3 mt-3">
                    <a 
                      href="/legal" 
                      className="text-[10px] text-slate-400 hover:text-primary transition-colors"
                      data-testid="legal-link"
                    >
                      Legal
                    </a>
                    <span className="text-slate-300">|</span>
                    <a 
                      href="/privacy" 
                      className="text-[10px] text-slate-400 hover:text-primary transition-colors"
                      data-testid="privacy-link"
                    >
                      Privacy
                    </a>
                    <span className="text-slate-300">|</span>
                    {/* v3.14-85: Subtle connection status */}
                    <button
                      type="button"
                      onClick={testConnection}
                      disabled={connectionStatus === 'checking'}
                      className="text-[10px] flex items-center gap-1 transition-colors"
                      data-testid="connection-status"
                      title={connectionStatus === 'failed' ? connectionDetails?.error : `Server: ${connectionDetails?.version || 'checking...'}`}
                    >
                      {connectionStatus === 'checking' && (
                        <span className="text-slate-400">Checking...</span>
                      )}
                      {connectionStatus === 'connected' && (
                        <>
                          <Wifi className="h-2.5 w-2.5 text-green-500" />
                          <span className="text-green-500">{connectionDetails?.version}</span>
                        </>
                      )}
                      {connectionStatus === 'failed' && (
                        <>
                          <WifiOff className="h-2.5 w-2.5 text-red-400" />
                          <span className="text-red-400 hover:text-red-500">Offline</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
    </PublicLayout>
  );
};

export default LoginPage;
